import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useContextAgent } from "./ContextAgentContext";

/**
 * Registers a scoped system agent with the app-level context rail (Option A).
 * Mount on builder pages; unregisters on unmount.
 */
export default function useRegisterContextAgent({
  enabled,
  agentName,
  scopeLabel,
  scopeId,
  launcherAgentId,
  attentionMessage,
  initialOpen = false,
  panel,
  onToggleClick,
  controlledOpen,
  controlledOnOpenChange,
}) {
  const { setRegistration, clearRegistration, setOpen, open } = useContextAgent();
  const autoOpenedRef = useRef(false);
  const prevScopeIdRef = useRef(scopeId);

  const applyOpen = useCallback(
    (next) => {
      if (controlledOnOpenChange) {
        controlledOnOpenChange(next);
        return;
      }
      setOpen(next);
    },
    [controlledOnOpenChange, setOpen]
  );

  useLayoutEffect(() => {
    if (!enabled) {
      clearRegistration();
      return;
    }

    setRegistration({
      agentName,
      scopeLabel,
      scopeId,
      launcherAgentId,
      attentionMessage,
      panel,
      onToggleClick,
      open: controlledOpen,
      onOpenChange: controlledOnOpenChange,
    });
  }, [
    enabled,
    agentName,
    scopeLabel,
    scopeId,
    launcherAgentId,
    attentionMessage,
    panel,
    onToggleClick,
    controlledOpen,
    controlledOnOpenChange,
    setRegistration,
    clearRegistration,
  ]);

  useLayoutEffect(
    () => () => {
      clearRegistration();
    },
    [clearRegistration]
  );

  useEffect(() => {
    if (!enabled) {
      autoOpenedRef.current = false;
      return;
    }
    if (initialOpen && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      applyOpen(true);
    }
  }, [enabled, initialOpen, applyOpen]);

  useEffect(() => {
    if (!enabled) return;
    if (prevScopeIdRef.current !== scopeId && prevScopeIdRef.current !== undefined) {
      applyOpen(false);
      autoOpenedRef.current = false;
    }
    prevScopeIdRef.current = scopeId;
  }, [enabled, scopeId, applyOpen]);

  return { open, setOpen: applyOpen };
}
